import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Drawer, Modal } from "antd";
import Button from "@dtci/spellbook/Button";
import { useJobsActions } from "tap/store/licensingJobs/useJobs.js";
import { tapApi } from "tap/api.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { getLicensingInactivateErrorMessage } from "tap/pages/LicensingJobs/utils/inactivateLicensingJobResult.js";
import { LicensingJobForm } from "./licensing-job-form";

/**
 * Transforms form data to API payload format.
 * Handles react-select format ({ value, label, data }) back to API format ({ id, name }).
 */
const formatFormDataForSave = (formData, selectedJob, isCreateMode) => {
  const formatArray = (arr) =>
    (arr || [])
      .filter((item) => item !== null && item !== undefined)
      .map((item) => {
        if (item.data) {
          return item.data;
        }
        return {
          id: item.value || item.id,
          name: item.label || item.name || null,
          titleWithId: item.titleWithId || null,
        };
      });

  if (isCreateMode) {
    return {
      catalogs: formatArray(formData.catalogs),
      deals: formatArray(formData.deals),
      medias: formatArray(formData.medias),
      territories: formatArray(formData.territories),
      startDate: formData.startDate || "",
      endDate: formData.endDate || "",
      description: formData.description || "",
      licenseeTypeList: formData.licenseeTypes || [],
      status: "CREATED",
      isTpr: formData.isTpr || false,
    };
  }

  return {
    ...selectedJob,
    description: formData.description || "",
    startDate: formData.startDate || "",
    endDate: formData.endDate || "",
    catalogs: formatArray(formData.catalogs),
    deals: formatArray(formData.deals),
    medias: formatArray(formData.medias),
    territories: formatArray(formData.territories),
    licenseeTypeList: formData.licenseeTypes || [],
    isTpr: formData.isTpr || false,
    newCatalogs: formatArray(formData.newCatalogs),
    formats: selectedJob?.formats || [],
  };
};

/**
 * Validates licensing job form. Deal field is optional; all other fields required.
 */
const validateForm = (formData) => {
  const errors = {};
  const hasValidItems = (arr) =>
    arr && arr.length > 0 && arr.some((item) => item !== null);

  if (!formData.description?.trim()) {
    errors.description = "Description is required";
  }
  if (!formData.startDate) {
    errors.startDate = "Start date is required";
  }
  if (!formData.endDate) {
    errors.endDate = "End date is required";
  }

  // Validate end date is not before start date
  if (formData.startDate && formData.endDate) {
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (end < start) {
      errors.endDate = "End Date cannot be before Start Date";
    }
  }

  if (!hasValidItems(formData.catalogs)) {
    errors.catalogs = "At least one catalog is required";
  } else if (formData.catalogs?.some((item) => item === null)) {
    errors.catalogs = "All catalog rows must have a value selected";
  }

  // Validate catalog limit
  const catalogCount =
    formData.catalogs?.filter((item) => item !== null)?.length || 0;
  if (catalogCount > 99999) {
    errors.catalogs =
      "A maximum of 99999 titles may be added to a Licensing Job";
  }

  // Validate new catalogs (edit mode) have no empty rows
  if (formData.newCatalogs?.some((item) => item === null)) {
    errors.newCatalogs = "All new catalog rows must have a value selected";
  }

  // Deals are optional, but any added rows must have a value selected
  if (
    formData.deals?.length > 0 &&
    formData.deals.some((item) => item === null)
  ) {
    errors.deals = "All deal rows must have a value selected";
  }

  if (!hasValidItems(formData.medias)) {
    errors.medias = "At least one media is required";
  } else if (formData.medias?.some((item) => item === null)) {
    errors.medias = "All media rows must have a value selected";
  }

  if (!hasValidItems(formData.territories)) {
    errors.territories = "At least one territory is required";
  } else if (formData.territories?.some((item) => item === null)) {
    errors.territories = "All territory rows must have a value selected";
  }
  if (!formData.licenseeTypes || formData.licenseeTypes.length === 0) {
    errors.licenseeTypes = "At least one licensee type is required";
  }

  return errors;
};

const getInitialFormState = () => ({
  description: "",
  startDate: "01/01/1900",
  endDate: "12/31/3000",
  catalogs: [],
  newCatalogs: [],
  deals: [],
  medias: [],
  territories: [],
  licenseeTypes: [],
});

/**
 * Transforms API job data to form format.
 * Converts { id, name } arrays to react-select format { value, label, data }.
 * Note: API returns licenseeTypeList, form uses licenseeTypes.
 */
const transformJobToFormData = (job) => {
  if (!job) return getInitialFormState();

  const transformArray = (arr, labelKey = "name") =>
    (arr || []).map((item) => ({
      value: item.id,
      label:
        item[labelKey] || item.name || item.titleWithId || `ID: ${item.id}`,
      data: item,
    }));

  const transformCatalogs = (arr) =>
    (arr || []).map((item) => ({
      value: item.id,
      label: item.titleWithId || item.name || `ID: ${item.id}`,
      data: item,
    }));

  return {
    description: job.description || "",
    startDate: job.startDate || "01/01/1900",
    endDate: job.endDate || "12/31/3000",
    catalogs: transformCatalogs(job.catalogs),
    newCatalogs: [],
    deals: transformArray(job.deals),
    medias: transformArray(job.medias),
    territories: transformArray(job.territories),
    licenseeTypes: job.licenseeTypeList || [],
    status: job.status || "",
    isTpr: job.isTpr || false,
    rlContactId: job.rlContactId || "",
    pmxReleaseId: job.pmxReleaseId || "",
  };
};

export const EditLicensingJobDrawer = () => {
  const { isEditDrawerOpen, selectedJob, licensingJobForm } = useSelector(
    (state) => state.licensingJobs,
  );
  const { updateJobs, updateJobsForm } = useJobsActions();
  const { showSuccessToast, showErrorToast } = useToasts();
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const [validationErrors, setValidationErrors] = useState({});

  const isCreateMode = !selectedJob?.id;

  useEffect(() => {
    if (isEditDrawerOpen) {
      setValidationErrors({});
      if (isCreateMode) {
        updateJobsForm({
          key: "licensingJobForm",
          data: getInitialFormState(),
        });
      } else if (selectedJob) {
        updateJobsForm({
          key: "licensingJobForm",
          data: transformJobToFormData(selectedJob),
        });
      }
    }
  }, [isEditDrawerOpen, isCreateMode, selectedJob]);

  const handleClose = () => {
    updateJobs({
      isEditDrawerOpen: false,
    });
    setValidationErrors({});
  };

  // Form reset deferred to afterOpenChange to allow close animation to complete
  const handleAfterOpenChange = (open) => {
    if (!open) {
      updateJobs({
        selectedJob: null,
      });
      updateJobsForm({
        key: "licensingJobForm",
        data: getInitialFormState(),
      });
    }
  };

  const handleRun = () => {
    if (!selectedJob?.id) return;

    Modal.confirm({
      title: "Run Job",
      content: "Are you sure you wish to run the licensing job?",
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        setLoadingTrue();
        try {
          const result = await tapApi.runLicensingJob(selectedJob.id);
          if (result === true) {
            showSuccessToast("Job running");
            handleClose();
            updateJobs({ refreshJobs: true });
          } else {
            showErrorToast("Job cannot be run");
          }
        } catch (error) {
          showErrorToast("Job cannot be run");
          console.error("Error running licensing job:", error);
        } finally {
          setLoadingFalse();
        }
      },
    });
  };

  const handleInactivate = () => {
    if (!selectedJob?.id) return;

    Modal.confirm({
      title: "Inactivate",
      content: "Are you sure you wish to inactive the licensing job?",
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        setLoadingTrue();
        try {
          const result = await tapApi.inactivateLicensingJob(selectedJob.id);
          const err = getLicensingInactivateErrorMessage(result);
          if (err) {
            showErrorToast(err);
          } else {
            showSuccessToast("Job was Inactivated");
            handleClose();
            updateJobs({ refreshJobs: true });
          }
        } catch (error) {
          showErrorToast("Job is running, cannot be inactivated!");
          console.error("Error inactivating licensing job:", error);
        } finally {
          setLoadingFalse();
        }
      },
    });
  };

  const handleSave = async () => {
    const formData = licensingJobForm;

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoadingTrue();

    try {
      const payload = formatFormDataForSave(
        formData,
        selectedJob,
        isCreateMode,
      );

      let result;
      if (isCreateMode) {
        result = await tapApi.createLicensingJob(payload);
      } else {
        result = await tapApi.updateLicensingJob(selectedJob.id, payload);
      }

      if (
        result?.code ||
        result?.httpCode ||
        result?.error ||
        result?.errors?.licensingError
      ) {
        showErrorToast(
          result?.errors?.licensingError ||
            result?.message ||
            result?.error?.message ||
            "Failed to save licensing job",
        );
      } else {
        showSuccessToast(
          isCreateMode
            ? "Licensing job created successfully"
            : "Licensing job updated successfully",
        );
        handleClose();
        updateJobs({ refreshJobs: true });
      }
    } catch (error) {
      console.error("Error saving licensing job:", error);
      showErrorToast("An error occurred while saving the licensing job");
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <Drawer
      className="create-drawer"
      title={
        isCreateMode
          ? "Create Licensing Job"
          : `Edit Licensing Job ${selectedJob?.id ? `- ${selectedJob.id}` : ""}`
      }
      width={800}
      placement="right"
      open={isEditDrawerOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      bodyStyle={{ padding: 0, backgroundColor: "#f5f5f5" }}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            iconType="fa-solid fa-xmark"
            styleType="link"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            iconType="fa-solid fa-save"
            styleType="link"
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      }
    >
      <LicensingJobForm
        mode={isCreateMode ? "create" : "edit"}
        validationErrors={validationErrors}
        onRun={handleRun}
        onInactivate={handleInactivate}
      />
    </Drawer>
  );
};
